import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2, Circle, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge, ReasonChips } from "@/components/recon/StatusBadge";
import { ReservaBadge } from "@/components/recon/ReservaBadge";
import { ProductThumb, StockCell } from "@/components/recon/ProductStock";
import { ColumnMenu } from "@/components/recon/ColumnMenu";
import { slaMeta, statusMeta, REASON_LABELS, SLA_FILTER_OPTIONS, RESERVA_OPTIONS } from "@/lib/labels";
import { useCatalogs } from "@/hooks/useCatalogs";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 25;

const EMPTY_COLF = { route: [], origin: [], delivery_type: [], status: [], reasons: [], sla: "", reserva_status: [] };

const COLS = [
  { label: "Order #", sort: "order_number" },
  { label: "Producto", sort: "model_number" },
  { label: "Ship-To", sort: "ship_to_name" },
  { label: "Ruta", sort: "route", filter: "route" },
  { label: "Origen", sort: "ship_from_location", filter: "origin" },
  { label: "Entrega", sort: "delivery_type", filter: "delivery_type" },
  { label: "Fecha", sort: "delivery_date" },
  { label: "Qty S/UI", sort: "storis.qty_ordered" },
  { label: "Stock" },
  { label: "Reserva", filter: "reserva_status" },
  { label: "Estatus", sort: "match_status", filter: "status" },
  { label: "SLA", filter: "sla" },
  { label: "Diferencias", filter: "reasons" },
];

function buildParams(runId, mode, filters, colFilters, sort, page, slaDays) {
  const p = { page, page_size: PAGE_SIZE };
  if (mode === "exceptions") p.only_exceptions = true;
  const status = colFilters.status.length ? colFilters.status.join(",") : filters.status;
  if (status) p.status = status;
  const route = colFilters.route.length ? colFilters.route.join(",") : filters.route;
  if (route) p.route = route;
  if (filters.order_type) p.order_type = filters.order_type;
  const origin = colFilters.origin.length ? colFilters.origin.join(",") : filters.origin;
  if (origin) p.origin = origin;
  const dtype = colFilters.delivery_type.length ? colFilters.delivery_type.join(",") : filters.delivery_type;
  if (dtype) p.delivery_type = dtype;
  if (filters.date_from) p.date_from = filters.date_from;
  if (filters.date_to) p.date_to = filters.date_to;
  if (filters.search) p.search = filters.search;
  const sla = colFilters.sla || filters.sla;
  if (sla) p.sla = sla;
  if (colFilters.reasons.length) p.reasons = colFilters.reasons.join(",");
  if (colFilters.reserva_status.length) p.reserva_status = colFilters.reserva_status.join(",");
  p.sla_days = slaDays || 7;
  if (sort.by) { p.sort_by = sort.by; p.sort_dir = sort.dir; }
  return p;
}

export function ResultsTable({ runId, mode, filters, onRowClick, slaDays = 7 }) {
  const qc = useQueryClient();
  const { routeName, locationName } = useCatalogs();
  const [page, setPage] = useState(1);
  const [colFilters, setColFilters] = useState(EMPTY_COLF);
  const [sort, setSort] = useState({ by: null, dir: "asc" });

  useEffect(() => { setPage(1); }, [filters, mode, runId, colFilters, sort]);

  const params = buildParams(runId, mode, filters, colFilters, sort, page, slaDays);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["results", runId, mode, params],
    queryFn: async () => (await api.get(`/recon/runs/${runId}/results`, { params })).data,
    enabled: !!runId,
    keepPreviousData: true,
  });

  const { data: opts } = useQuery({
    queryKey: ["filter-options", runId],
    queryFn: async () => (await api.get(`/recon/runs/${runId}/filter-options`)).data,
    enabled: !!runId,
  });

  const setColF = (key, val) => setColFilters((c) => ({ ...c, [key]: val }));
  const onSort = (by, dir) => setSort((s) => (s.by === by && s.dir === dir) ? { by: null, dir: "asc" } : { by, dir });
  const optionsFor = (key) => {
    const o = opts || {};
    if (key === "route") return (o.routes || []).map((v) => ({ value: v, label: routeName(v) ? `${v} · ${routeName(v)}` : v }));
    if (key === "origin") return (o.origins || []).map((v) => ({ value: v, label: locationName(v) ? `${v} · ${locationName(v)}` : v }));
    if (key === "delivery_type") return (o.delivery_types || []).map((v) => ({ value: v, label: v === "TRANSFER DELIVERY" ? "Transfer" : "Regular" }));
    if (key === "status") return (o.statuses || []).map((v) => ({ value: v, label: statusMeta(v).label }));
    if (key === "reasons") return (o.reasons || []).map((v) => ({ value: v, label: REASON_LABELS[v] || v }));
    if (key === "reserva_status") return RESERVA_OPTIONS;
    if (key === "sla") return SLA_FILTER_OPTIONS;
    return [];
  };

  const reviewMut = useMutation({
    mutationFn: (id) => api.patch(`/recon/results/${id}/review`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["results", runId] });
      toast.success("Estado de revisión actualizado");
    },
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isExc = mode === "exceptions";

  const hasDate = (r) => r.mismatch_reasons?.includes("DIFERENCIA_FECHA");
  const hasQty = (r) => r.mismatch_reasons?.includes("DIFERENCIA_CANTIDAD");

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-sm" data-testid={isExc ? "exceptions-table" : "results-table"}>
      <div className="overflow-auto thin-scroll">
        <table className="w-full text-sm border-collapse min-w-[980px]">
          <thead>
            <tr className="bg-slate-50 border-b border-[#E2E8F0] text-left">
              {COLS.map((c) => (
                <th key={c.label} className="px-3 py-2.5 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">
                  <span className="inline-flex items-center">
                    {c.label}
                    {(c.sort || c.filter) && (
                      <ColumnMenu sortKey={c.sort} sortBy={sort.by} sortDir={sort.dir} onSort={onSort}
                        filterKey={c.filter} options={c.filter ? optionsFor(c.filter) : []}
                        single={c.filter === "sla"} selected={c.filter ? colFilters[c.filter] : undefined}
                        onFilter={(v) => setColF(c.filter, v)} />
                    )}
                  </span>
                </th>
              ))}
              {isExc && <th className="px-3 py-2.5 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">Acción</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={15} className="py-16 text-center text-slate-400">
                <Loader2 className="animate-spin inline" size={20} />
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={15} className="py-16 text-center text-slate-400">
                <Inbox className="inline mb-2" size={26} /><div className="text-sm">Sin resultados para los filtros aplicados</div>
              </td></tr>
            ) : (
              items.map((r, i) => {
                const sQty = r.storis?.qty_ordered;
                const uQty = r.ui?.qty_ordered;
                return (
                  <tr key={r.id}
                    data-testid={`result-row-${r.id}`}
                    onClick={() => onRowClick(r)}
                    className={`border-b border-slate-100 cursor-pointer hover:bg-slate-100 ${i % 2 ? "bg-slate-50/40" : "bg-white"} ${r.reviewed ? "opacity-60" : ""}`}>
                    <td className="px-3 py-2 font-mono text-slate-900 whitespace-nowrap">{r.order_number || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <ProductThumb src={r.image_url} />
                        <div className="min-w-0">
                          <div className="font-mono text-slate-700 text-xs whitespace-nowrap">{r.model_number || "—"}</div>
                          {r.product_description && (
                            <div className="text-[11px] text-slate-400 truncate max-w-[150px]" title={r.product_description}>{r.product_description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[160px] truncate">{r.ship_to_name || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-600" title={routeName(r.route) || ""}>{r.route || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[160px] truncate" title={r.ship_from_location || ""}>{r.ship_from_name || locationName(r.ship_from_location) || r.ship_from_location || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-sm border ${r.delivery_type === "TRANSFER DELIVERY" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                        {r.delivery_type === "TRANSFER DELIVERY" ? "Transfer" : "Regular"}
                      </span>
                    </td>
                    <td className={`px-3 py-2 font-mono whitespace-nowrap ${hasDate(r) ? "bg-[#FEE2E2] text-[#991B1B] font-semibold" : "text-slate-600"}`}>
                      {r.delivery_date || "—"}
                    </td>
                    <td className={`px-3 py-2 font-mono whitespace-nowrap ${hasQty(r) ? "bg-[#FEE2E2] text-[#991B1B] font-semibold" : "text-slate-500"}`}>
                      {sQty ?? "—"}<span className="text-slate-300"> / </span>{uQty ?? "—"}
                    </td>
                    <td className="px-3 py-2"><StockCell r={r} /></td>
                    <td className="px-3 py-2"><ReservaBadge code={r.reserva_status} title={(r.linked_transfers && r.linked_transfers.length) ? `Transfer: ${r.linked_transfers.map((t) => t.order_number).join(", ")}` : undefined} /></td>
                    <td className="px-3 py-2"><StatusBadge code={r.match_status} reasons={r.mismatch_reasons} /></td>
                    <td className="px-3 py-2">
                      {(() => {
                        const m = slaMeta(r.sla_status);
                        if (!m) return <span className="text-slate-300">—</span>;
                        return (
                          <span data-testid={`sla-badge-${r.id}`} className="inline-flex items-center rounded-sm font-semibold whitespace-nowrap border px-2 py-0.5 text-[11px]"
                            style={{ background: m.bg, color: m.text, borderColor: m.border }}>
                            {m.label}{r.sla_status === "FUERA_SLA" && r.days_overdue ? ` ·${r.days_overdue}d` : ""}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2"><ReasonChips reasons={r.mismatch_reasons} /></td>
                    {isExc && (
                      <td className="px-3 py-2">
                        <Button size="sm" variant={r.reviewed ? "secondary" : "outline"}
                          data-testid={`review-button-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); reviewMut.mutate(r.id); }}
                          className="h-7 rounded-sm text-xs gap-1">
                          {r.reviewed ? <CheckCircle2 size={13} className="text-emerald-600" /> : <Circle size={13} />}
                          {r.reviewed ? "Revisado" : "Marcar"}
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E2E8F0] text-xs text-slate-500">
        <span className="font-mono" data-testid="results-count">
          {total === 0 ? "0 resultados" : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}`}
          {isFetching && <Loader2 className="inline ml-2 animate-spin" size={12} />}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" data-testid="page-prev" disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))} className="h-7 w-7 p-0 rounded-sm">
            <ChevronLeft size={15} />
          </Button>
          <span className="font-mono px-2">{page}/{totalPages}</span>
          <Button variant="outline" size="sm" data-testid="page-next" disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="h-7 w-7 p-0 rounded-sm">
            <ChevronRight size={15} />
          </Button>
        </div>
      </div>
    </div>
  );
}
